import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import Comments from 'components/UI/ChatBox/Comments';
import store from 'stores/RootStore';
import { PanelViews } from 'types/PanelViews';
import {
  dragHelper,
  eventDataTransferHasFiles,
  eventDataTransferHasSingleFile,
} from 'utils/DragHelper';

import AdList from './AdList/AdList';
import AdView from './AdView/AdView';
import AssetsDrawer from './Drawers/AssetsDrawer/AssetsDrawer';
import UploadAssetModal from './Drawers/AssetsDrawer/UploadAssetModal';
import BundlesDrawer from './Drawers/BundlesDrawer/BundlesDrawer';
import FiltersDrawer from './Drawers/FiltersDrawer/FiltersDrawer';
import PropertiesDrawer from './Drawers/PropertiesDrawer/PropertiesDrawer';
import TearsheetsDrawer from './Drawers/TearsheetsDrawer/TearsheetsDrawer';
import TemplatesDrawer from './Drawers/TemplatesDrawer/TemplatesDrawer';
import UploadTemplateModal from './Drawers/TemplatesDrawer/UploadTemplateModal';
import VideoBundlesDrawer from './Drawers/VideoBundlesDrawer/VideoBundlesDrawer';

const Container = styled.div.attrs({
  className: 'creative-root',
})`
  display: grid;
  grid-template-columns: 500px 1fr;
  height: 100%;
  position: relative;
`;

function isZipFile(theFile: File) {
  return theFile.type === 'application/zip' || /\.zip$/i.test(theFile.name);
}

function Creative() {
  const store_ui = store.ui;
  const store_current = store.current;
  const store_creative = store.creative;
  const store_user = store.user;
  const currentAd = store_creative?.ads?.current;

  const displayPanelBeforeDrag = useRef(PanelViews.NONE);

  useEffect(() => {
    if (store_current?.data.name) {
      document.title = store_current.data.name;
    }
  }, [store_current?.data.name]);

  // ==========================================================================
  // The headline: This is why store.* should arrive as props, not direct
  // imports!
  //
  // This note used to make that case from a specific bug: `creative` was
  // declared on RootStore with no initializer, so under
  // useDefineForClassFields:false no own property existed when
  // becomeObservable() took its Reflect.ownKeys snapshot. Assigning it raised
  // no reaction, and the only reason this component ever saw a new creative
  // was that loadCreative called setCurrent() - which IS observable - on the
  // very next line.
  //
  // Fixed in 6163a9a4, and the whole fix is the initializer in
  // `creative: Creative | undefined = undefined`. Worth knowing that is not
  // decorative: client/tsconfig.json sets useDefineForClassFields:false, so a
  // bare declaration emits no code at all and the property does not exist
  // until something assigns it. Tidy the `= undefined` away as redundant and
  // observability quietly goes with it, taking every dep array below.
  //
  // So the fragility moved rather than went away, and the argument for props
  // outlives it:
  //
  //   - <Creative /> takes no props, so nothing at the call site - and
  //     nothing in a test - records that this component depends on
  //     store.creative at all.
  //   - The re-render now hangs on `observer` staying wrapped around the
  //     export. Drop it and these effects still satisfy exhaustive-deps while
  //     quietly ceasing to fire.
  //   - Two mechanisms have to agree: MobX observability upstream, React dep
  //     arrays here. A prop would be one mechanism, and a checked one.
  //
  // Nowhere near reach yet, but this is the motivation when it comes up.
  // ==========================================================================

  // The callbacks are written inline here rather than hoisted to render scope,
  // which keeps the dep array self-policing: anything from render scope they
  // touch is lexically inside the effect, so exhaustive-deps demands it here
  // rather than relying on anyone remembering. dragHelper holds them for the
  // life of one registration, so their identity never needs pinning.
  //
  // `store_ui.fileDragging` is the seam with the legacy UI store, and this is
  // its only writer. Four unrelated components read it to show drop
  // indicators -- both upload drawers, the Animation drawer, and AdViewFrame's
  // empty placeholder -- and have no other way to know a drag is in progress.
  // Purely cosmetic: nothing branches on it for behaviour, so the worst a
  // stomped value costs is a missing or lingering overlay.
  //
  // It is cleared on all three terminal paths. `drop` is the terminal event
  // for an OS file drag (no dragleave follows it, and dragend only fires for
  // drags that started inside the page), so clearing it in onLastDragLeave
  // alone would leave the flag stuck true after every accepted drop.
  useEffect(() => {
    if (!store_creative) return;

    return dragHelper({
      target: window,
      preFilter: eventDataTransferHasFiles,
      filter: eventDataTransferHasSingleFile,
      dropEffect: 'copy',
      onFirstDragEnter: () => {
        store_ui.fileDragging = true;

        const initialDisplayPanel =
          store_creative.displayPanel || PanelViews.NONE;
        displayPanelBeforeDrag.current = initialDisplayPanel;

        if (
          initialDisplayPanel !== PanelViews.ASSETS &&
          initialDisplayPanel !== PanelViews.TEMPLATES
        ) {
          const creativeHasTemplates =
            store_creative.templates.all.length !== 0;
          return store_creative.setPanel(
            creativeHasTemplates ? PanelViews.ASSETS : PanelViews.TEMPLATES
          );
        }
      },
      onLastDragLeave: () => {
        store_ui.fileDragging = false;
        store_creative.setPanel(displayPanelBeforeDrag.current);
      },
      onDropAccepted: (event) => {
        store_ui.fileDragging = false;

        const [theFile] = event.dataTransfer!.files;

        const itsAZip = isZipFile(theFile);
        const targetPanel = itsAZip ? PanelViews.TEMPLATES : PanelViews.ASSETS;

        // When accepting the drop, don't restore the former panel
        displayPanelBeforeDrag.current = targetPanel;

        store_creative.setPanel(targetPanel);
        const targetUploadManager = itsAZip
          ? store_creative.templates.uploadManager
          : store_creative.assets.uploadManager;
        targetUploadManager.file = theFile;
        targetUploadManager.modalVisible = true;
      },
      onDragEnd: () => {
        store_ui.fileDragging = false;
        store_creative.setPanel(displayPanelBeforeDrag.current);
      },
    });
  }, [store_creative, store_ui]);

  // Teardown the class version did in componentWillUnmount. Not incidental
  // cleanup: loadCreative early-returns while `store.creative.mounted` is true,
  // and reset() is what clears that flag. Its only other caller,
  // RootStore.reset(), is itself never called anywhere in src -- so without
  // this, navigating from one creative to another leaves you looking at the
  // previous creative's data.
  //
  // Depends on [store_creative] rather than [] so the cleanup closes over the
  // creative it was set up for. With [] it would capture the first render's
  // value, which is undefined, and reset nothing.
  useEffect(() => {
    return () => {
      store_creative?.reset();
    };
  }, [store_creative]);

  const isPanelOpen =
    store_creative &&
    store_creative === store_current &&
    store_creative.displayPanel !== PanelViews.NONE;

  return (
    <>
      {store_creative && (
        <Container>
          <AdList />
          <AdView />

          <AssetsDrawer />
          <PropertiesDrawer />
          <BundlesDrawer />
          <FiltersDrawer />
          <TearsheetsDrawer />
          <TemplatesDrawer />
          <VideoBundlesDrawer />

          <UploadAssetModal />
          <UploadTemplateModal />
          {currentAd && (
            <Comments
              isPanelOpen={isPanelOpen}
              parentDoc={currentAd}
              user={store_user}
              variant="floating"
            />
          )}
        </Container>
      )}
    </>
  );
}

export default observer(Creative);
